import React, { useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';
import Tab from 'tbk-components/src/components/Tab';
import Tabs from 'tbk-components/src/components/Tabs';
import AnimatedArcs from '../../common/AnimatedArcs';

export interface ITabbedProductCategoriesTilesProps {
	heading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	image?: IImage;
	ctaModal?: boolean;
	/**
	 * CTA properties used for the modal
	 */
	cta?: ILink;

}

export interface  ITabbedProductCategoriesTabsProps {
	label: string;
	tiles?: ITabbedProductCategoriesTilesProps[];
	cta?: ILink;
}

export interface ITabbedProductCategoriesProps extends IBaseElementProps {
	style: 'Default' | 'Dark';
	heading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	tabs?: ITabbedProductCategoriesTabsProps[];
	enableLineAnimations?: boolean;
}

/**
 * Calian Tabbed Product Categories
 * @icon format-gallery
 * @block
 */
const TabbedProductCategories: React.FC<ITabbedProductCategoriesProps> = ({
	style = 'Default',
	id,
	className,
	classNames = [],
	heading,
	description,
	tabs,
	enableLineAnimations = false,
}) => {
	const [showModal, setShowModal] = useState(false);
	const [modalHeading, setModalHeading] = useState<string>('');
	const [modalDescription, setModalDescription] = useState<string>('');
	const [modalCTALabel, setModalCTALabel] = useState<string>('');
	const [modalCTAHref, setModalCTAHref] = useState<string>('');
	const [modalCTATarget, setModalCTATarget] = useState<string>('');
	const [modalImage, setModalImage] = useState<string>('');

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-tabbed-product-categories position-relative',
					...classNames,
				)
			}
		>
			<div
				className={cn(
					'calian-tabbed-product-categories__container position-relative py-3 py-5 py-lg-12',
					style === 'Dark' &&
						'calian-tabbed-product-categories__container--dark',
				)}
			>
				<div className="calian-tabbed-product-categories__content mx-auto position-relative z-2 px-3">
					{heading && <h2 className="text-center">{heading}</h2>}
					{description && (
						<div
							dangerouslySetInnerHTML={{
								__html: description,
							}}
							className="text-center"
						/>
					)}
				</div>

				{tabs && tabs.length > 0 && (
					<Tabs className="calian-tabbed-product-categories__tabs position-relative z-2 mx-auto">
						{tabs &&
							tabs.length > 0 &&
							tabs.map((tab, index) => (
								<Tab title={tab.label} key={index}>
									<div className="calian-tabbed-product-categories__tiles">
										{tab.tiles &&
											tab.tiles.length > 0 &&
											tab.tiles.map((tile, index) => (
												<div
													className="calian-tabbed-product-categories__tile"
													key={index}
												>
													{tile.ctaModal ? (
														<button
															onClick={() => {
																setShowModal(
																	true,
																);
																setModalHeading(
																	tile.heading,
																);
																setModalDescription(
																	tile.description,
																);
																setModalCTALabel(
																	tile.cta?.label,
																);
																setModalCTAHref(
																	tile.cta?.href,
																);
																setModalCTATarget(
																	tile.cta?.target,
																);
																setModalImage(
																	tile.image?.src,
																);
															}}
															className="d-flex text-decoration-none border-0 p-0 align-items-center"
														>
															<div className="calian-tabbed-product-categories__tile-heading d-flex align-items-center mx-5 my-4 me-3 text-left">
																{tile.heading}
															</div>
															<div className="calian-tabbed-product-categories__tile-image ms-auto">
																<Image
																	{...tile.image}
																/>
															</div>
														</button>
													) : (
														<a
															href={tile.cta?.href}
															target={
																tile.cta?.target
															}
															rel={
																tile.cta?.target ===
																'_blank'
																	? 'noopener noreferrer'
																	: ''
															}
															aria-label={
																tile.cta?.label
															}
															className="d-flex text-decoration-none border-0 p-0 align-items-center"
														>
															<div className="calian-tabbed-product-categories__tile-heading d-flex align-items-center mx-5 my-4 me-3 text-left">
																{tile.heading}
															</div>
															<div className="calian-tabbed-product-categories__tile-image ms-auto">
																<Image
																	{...tile.image}
																/>
															</div>
														</a>
													)}
												</div>
											))}
									</div>
									{(tab?.cta?.label || tab?.cta?.href) && (
										<div className="d-flex justify-content-center mt-9">
											<Button
												className="btn btn-dark"
												{...tab.cta}
											/>
										</div>
									)}
								</Tab>
							))}
					</Tabs>
				)}
			</div>
			{enableLineAnimations && (
				<AnimatedArcs
					arcsStyle={'Component - Two Lines'}
					arcsPosition="Right"
					arcOneColor={style == 'Dark' ? '#FFFFFF' : '#FFFFFF'}
					arcOneStrokeWidth={4}
					arcTwoColor={style == 'Dark' ? '#1F378C' : '#2308C9'}
					arcTwoStrokeWidth={3}
					animationDelay={250}
					animationOffset={150}
				/>
			)}
			{showModal && (
				<Modal
					onCancel={function () {
						setShowModal(false);
					}}
					modalClassNames={[
						'calian-tabbed-product-categories__modal',
						'modal-dialog',
						'modal-dialog-centered',
						'modal-lg',
						,
					]}
					headerClassNames={[
						'border-0 position-absolute w-100 top-0 right-0 z-3',
					]}
					bodyClassNames={['modal-body']}
					trapFocus
				>
					<div className="d-flex gap-6 gap-lg-7 flex-column flex-md-row">
						<div className="d-flex flex-column gap-3 order-2 order-lg-1">
							{modalHeading && (
								<h3 className="mb-0">{modalHeading}</h3>
							)}
							{modalDescription && (
								<div
									dangerouslySetInnerHTML={{
										__html: modalDescription,
									}}
								/>
							)}
							<div className="mt-2">
								<a
									href={modalCTAHref}
									target={modalCTATarget}
									rel={
										modalCTATarget === '_blank'
											? 'noopener noreferrer'
											: ''
									}
									className="btn btn-sm btn-primary"
								>
									{modalCTALabel}
								</a>
							</div>
						</div>
						<div className="modal-image order-1 order-lg-2">
							<img src={modalImage} alt={modalHeading} />
						</div>
					</div>
				</Modal>
			)}
		</div>
	);
};
export default TabbedProductCategories;
